import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/config';
import '../styles/Verify.css';
import MathRenderer, { hasMath } from '../components/MathRenderer';

// -- Constants -----------------------------------------------------------------
const EXAM_TYPES  = ['KCET','JEE','JEE Mains','JSAT','Other'];
const BLOOMS_OPTS = [
  { value:'remember',   label:'1 - Remember'  },
  { value:'understand', label:'2 - Understand' },
  { value:'apply',      label:'3 - Apply'      },
  { value:'analyse',    label:'4 - Analyse'    },
  { value:'evaluate',   label:'5 - Evaluate'   },
  { value:'create',     label:'6 - Create'     },
];
const DIFF_OPTS = ['low','medium','high'];

function toTitleCase(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function getUserSubjectId(user) {
  if (!user?.department) return null;
  const dept       = user.department.split(',')[0].trim();
  const normalized = dept.charAt(0).toUpperCase() + dept.slice(1).toLowerCase();
  const map        = { Physics: 1, Chemistry: 2, Mathematics: 3 };
  return map[normalized] || null;
}

// -- Header --------------------------------------------------------------------
function Header({ user, onLogout }) {
  const subjects = user?.subjects?.map(s => s.name).join(', ') || user?.department || '';
  return (
    <header className="ca-header">
      <div className="ca-header-left" />
      <div className="ca-header-center">
        <h1 className="ca-header-title">Question Bank Verification</h1>
      </div>
      <div className="ca-header-right">
        <span className="ca-header-user">{user?.name}</span>
        {subjects && <span className="ca-header-subjects">{subjects}</span>}
        <button className="ca-logout-link" onClick={onLogout}>Logout</button>
      </div>
    </header>
  );
}

// -- Nav Tabs ------------------------------------------------------------------
function NavTabs({ view, setView }) {
  return (
    <nav className="ca-nav">
      {[
        { key:'dashboard', label:'Dashboard'            },
        { key:'verify',    label:'Verify Question Bank' },
        { key:'history',   label:'History'              },
      ].map(t => (
        <button key={t.key}
          className={`ca-nav-tab ${view === t.key ? 'ca-nav-tab--active' : ''}`}
          onClick={() => setView(t.key)}>
          {t.label}
        </button>
      ))}
    </nav>
  );
}

// -- Module dropdown with "Others" ---------------------------------------------
function ModuleSelect({ value, subjectId, onChange, modules, onModuleCreated }) {
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const subjectIdStr = subjectId != null && subjectId !== '' ? String(subjectId) : '';
  const filtered     = modules.filter(m =>
    !subjectIdStr || String(m.subject_id) === subjectIdStr
  );
  const displayVal = showNew ? '__others__' : (value || '');

  const handleChange = (e) => {
    if (e.target.value === '__others__') { setShowNew(true); setNewName(''); setError(''); }
    else { setShowNew(false); onChange(e.target.value); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { setError('Enter a module name'); return; }
    if (!subjectIdStr) {
      setError('No subject found for this question — contact admin');
      return;
    }
    setSaving(true); setError('');
    try {
      const res = await API.post('/api/verify/modules', {
        name: newName.trim(),
        subject_id: subjectIdStr,
      });
      const mod = res.data.module;
      onModuleCreated(mod);
      onChange(String(mod.module_id));
      setShowNew(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <select className="ca-select ca-select--full" value={displayVal} onChange={handleChange}>
        <option value="">— Select Module —</option>
        {filtered.map(m => (
          <option key={m.module_id} value={String(m.module_id)}>{m.name}</option>
        ))}
        <option value="__others__">Others (create new…)</option>
      </select>
      {showNew && (
        <div className="ca-module-new">
          <input className="ca-input ca-module-new-input" placeholder="New module name"
            value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()} />
          <button className="ca-btn ca-btn--sm ca-btn--primary" onClick={handleCreate} disabled={saving}>
            {saving ? '…' : 'Add'}
          </button>
          <button className="ca-btn ca-btn--sm"
            onClick={() => { setShowNew(false); onChange(value); }}>Cancel</button>
          {error && <span className="ca-err-inline">{error}</span>}
        </div>
      )}
    </div>
  );
}

// -- MathField ----------------------------------------------------------------
function MathField({ value, onChange, placeholder, multiline, className, subjectId, disabled }) {
  const [editing, setEditing] = useState(false);
  const inputRef  = useRef(null);
  const plainRef  = useRef(null);
  const mathDetected = hasMath(value, subjectId);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.tagName === 'TEXTAREA') {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      }
    }
  }, [editing]);

  useEffect(() => {
    const el = plainRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, [value, multiline]);

  if (disabled) {
    if (mathDetected) return <MathRenderer text={value} subjectId={subjectId} block={multiline} />;
    const Tag = multiline ? 'div' : 'span';
    return (
      <Tag className={`${className || ''} ca-math-static`}
        style={{ display: multiline ? 'block' : 'inline-block', width: '100%' }}>
        {value || ''}
      </Tag>
    );
  }

  if (editing || !mathDetected) {
    const props = {
      ref: inputRef,
      value: value || '',
      placeholder,
      onChange: e => onChange(e.target.value),
      onBlur: () => setEditing(false),
      onInput: e => {
        e.target.style.height = 'auto';
        e.target.style.height = e.target.scrollHeight + 'px';
      },
    };
    if (multiline) return <textarea className={className} rows={2} {...props} />;
    return <input type="text" className={className} {...props} />;
  }

  return (
    <div onClick={() => setEditing(true)} title="Click to edit" style={{ cursor: 'text', width: '100%' }}>
      <MathRenderer text={value} subjectId={subjectId} block={multiline} />
    </div>
  );
}

// -- Feedback Modal -----------------------------------------------------------
function FeedbackModal({ question, existingFeedback, onClose, onSaved, readOnly = false }) {
  const [note,    setNote]    = useState(existingFeedback?.note || '');
  const [pdfFile, setPdfFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const fileRef = useRef(null);
  const API_BASE = API.defaults.baseURL;

  const handleSave = async () => {
    if (!note.trim() && !pdfFile) { setErr('Please add a note or upload a PDF.'); return; }
    setSaving(true); setErr('');
    try {
      const formData = new FormData();
      formData.append('question_id', question.question_id);
      formData.append('note', note.trim());
      if (pdfFile) formData.append('feedback_pdf', pdfFile);
      await API.post('/api/verify/questions/feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved({ note: note.trim(), hasPdf: !!pdfFile });
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="ca-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ca-modal ca-modal--feedback">
        <div className="ca-modal-head">
          <h3>
            {readOnly ? '👁️ Feedback — ' : 'Feedback For '}
            Q{question.question_number ?? question.question_id}
            {readOnly && (
              <span style={{
                marginLeft: 10, fontSize: 11, fontWeight: 700,
                background: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a', borderRadius: 6,
                padding: '2px 8px', verticalAlign: 'middle',
              }}>Read Only</span>
            )}
          </h3>
          <button className="ca-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ca-modal-body">
          {/* Reverification note from admin — show as an info banner */}
          {readOnly && question.reverification_note && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              borderRadius: 8, padding: '10px 14px', marginBottom: 4,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 4 }}>
                📝 Admin Note (Why This Was Sent Back)
              </span>
              <p style={{ margin: 0, fontSize: 14, color: '#1e293b', lineHeight: 1.6 }}>
                {question.reverification_note}
              </p>
            </div>
          )}

          <div className="ca-feedback-qpreview">
            <span className="ca-label">Question</span>
            {hasMath(question.question_text, question.subject_id)
              ? <MathRenderer text={question.question_text} subjectId={question.subject_id} block={true} className="ca-feedback-qtext" />
              : <p className="ca-feedback-qtext">{question.question_text}</p>
            }
          </div>

          {/* Note — editable for normal, plain text for reverification */}
          <div className="ca-field">
            <label className="ca-label">
              {readOnly ? 'Previously Saved Note' : 'Your Note / Remark'}
            </label>
            {readOnly ? (
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 14, color: note ? '#1e293b' : '#94a3b8',
                lineHeight: 1.6, minHeight: 80,
              }}>
                {note || 'No note saved.'}
              </div>
            ) : (
              <textarea className="ca-textarea ca-feedback-note" rows={5}
                placeholder="Scribble down your feedback, corrections, or remarks..."
                value={note} onChange={e => setNote(e.target.value)} />
            )}
          </div>

          {/* PDF — view only for reverification, upload zone for normal */}
          <div className="ca-field">
            <label className="ca-label">
              {readOnly ? 'Attached PDF' : 'Attach PDF (Optional)'}
            </label>
            {readOnly ? (
              existingFeedback?.feedback_pdf ? (
                <a
                  href={`${API_BASE}${existingFeedback.feedback_pdf}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ca-pdf-link"
                >
                  📄 View Saved PDF
                </a>
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No PDF attached.</p>
              )
            ) : (
              <>
                <div className="ca-pdf-upload-zone" onClick={() => fileRef.current?.click()}>
                  {pdfFile ? (
                    <div className="ca-pdf-chosen">
                      <span className="ca-pdf-icon">📄</span>
                      <span className="ca-pdf-name">{pdfFile.name}</span>
                      <button className="ca-pdf-remove"
                        onClick={e => { e.stopPropagation(); setPdfFile(null); }}>✕</button>
                    </div>
                  ) : (
                    <div className="ca-pdf-empty">
                      <span className="ca-pdf-icon">📎</span>
                      <span>Click To Upload A PDF</span>
                      <span className="ca-pdf-hint">Max 10 MB</span>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="application/pdf" style={{ display:'none' }}
                    onChange={e => { if (e.target.files[0]) setPdfFile(e.target.files[0]); }} />
                </div>
                {existingFeedback?.feedback_pdf && !pdfFile && (
                  <div className="ca-pdf-existing">
                    <span className="ca-pdf-icon">📄</span>
                    <span>Previously Uploaded PDF On File</span>
                    <span className="ca-pdf-hint">(Uploading a new one will replace it)</span>
                  </div>
                )}
                {existingFeedback?.feedback_pdf && (
                  <div style={{ marginTop: 6 }}>
                    <a
                      href={`${API_BASE}${existingFeedback.feedback_pdf}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ca-pdf-link"
                    >
                      📄 View Existing PDF
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="ca-modal-foot">
          {err && <span className="ca-err-inline">{err}</span>}
          <button className="ca-btn" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button className="ca-btn ca-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Feedback'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// -- Question Card ------------------------------------------------------------
function QuestionCard({ q, idx, draft, onChange, modules, onModuleCreated, fallbackSubjectId, lockError }) {
  const optLabels = ['a','b','c','d'];
  const optFields = ['option_a','option_b','option_c','option_d'];

  const subjectId       = q.subject_id || fallbackSubjectId || '';
  const curAnswer       = (draft.correct_answer || q.correct_answer || '').toLowerCase();
  const curDiff         = draft._diff_touched
    ? draft.difficulty_level
    : (draft._diff_db || q.difficulty_level || '');
  const questionText    = draft.question_text ?? q.question_text ?? '';
  const explanationText = draft.explanation !== undefined ? draft.explanation : (q.explanation || '');

  const isReadOnly = [1, 2, 3].includes(Number(subjectId));
  const editLock   = isReadOnly;
  const qLock      = true;

  const [showFeedback,  setShowFeedback]  = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(
    !!(q.feedback?.note || q.feedback?.feedback_pdf)
  );

  const moduleVal  = draft.module_id !== undefined ? draft.module_id : String(q.module_id || '');
  const bloomsVal  = draft.blooms_index !== undefined ? draft.blooms_index : (q.blooms_index || '');
  const diffVal    = draft.difficulty_level || draft._diff_db || q.difficulty_level || '';
  const canVerify  = !!(moduleVal && diffVal && bloomsVal);

  const handleVerifyToggle = (checked) => {
    if (checked && !canVerify) return;
    onChange(q.question_id, 'is_verified', checked);
  };

  const diffColors = {
    low:    { bg: '#dcfce7', border: '1px solid #16a34a', color: '#15803d' },
    medium: { bg: '#fef3c7', border: '1px solid #d97706', color: '#92400e' },
    high:   { bg: '#fee2e2', border: '1px solid #dc2626', color: '#991b1b' },
  };

  return (
    <div className={`ca-qcard ${draft.is_verified ? 'ca-qcard--verified' : ''}`}>

      {lockError && (
        <div className="ca-qcard-lock-banner">🔒 {lockError}</div>
      )}

      {/* ✅ Reverification banner — shown above the question so the verifier
          sees the admin's note before reading the question content. */}
      {q.needs_reverification && (
        <div style={{
          background:'#fef3c7', border:'1px solid #d97706',
          borderRadius:8, padding:'10px 14px', margin:'10px 20px 0',
          fontSize:13, color:'#92400e', fontWeight:600,
        }}>
          ⚠️ Admin has edited this question and requested reverification.
          {q.reverification_note && (
            <div style={{ fontWeight:400, marginTop:4 }}>
              Note: {q.reverification_note}
            </div>
          )}
        </div>
      )}

      <div className="ca-qcard-meta-row">
        {q.subject_name && <span className="ca-tag ca-tag--subject">{q.subject_name}</span>}
        {q.exam_type    && <span className="ca-tag">{q.exam_type}</span>}
        {q.exam_year    && <span className="ca-tag">{q.exam_year}</span>}
        {q.module_name  && <span className="ca-tag ca-tag--mod">{q.module_name}</span>}
        {!q.subject_id  && <span className="ca-tag ca-tag--warn">No Subject In DB</span>}
      </div>

      <div className={`ca-qcard-body ${lockError ? 'ca-qcard-body--locked' : ''}`}>
        {/* LEFT */}
        <div className="ca-qcard-left">
          <div className="ca-qcard-qrow">
            <span className="ca-qnum">Q{q.question_number ?? q.question_id}.</span>
            <div className="ca-qcard-qtext-wrap">
              <MathField
                value={questionText}
                onChange={val => onChange(q.question_id, 'question_text', val)}
                placeholder="Question text..."
                multiline={true}
                className="ca-qplain"
                subjectId={subjectId}
                disabled={qLock}
              />
            </div>
          </div>

          {q.question_image && (
            <img className="ca-qimg" src={q.question_image} alt="question" />
          )}

          <div className="ca-opts-section">
            <div className="ca-opts-grid">
              {optLabels.map((lbl, i) => {
                const field     = optFields[i];
                const val       = draft[field] !== undefined ? draft[field] : (q[field] || '');
                const isCorrect = curAnswer === lbl;
                return (
                  <div key={lbl} className={`ca-opt-inline ${isCorrect ? 'ca-opt-inline--correct' : ''}`}>
                    <span className="ca-opt-lbl">{lbl.toLowerCase()})</span>
                    <MathField
                      value={val}
                      onChange={v => onChange(q.question_id, field, v)}
                      placeholder={`Option ${lbl.toUpperCase()}`}
                      multiline={false}
                      className="ca-opt-plain"
                      subjectId={subjectId}
                      disabled={editLock}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ca-expl-section">
            <span className="ca-label">Explanation</span>
            <MathField
              value={explanationText}
              onChange={val => onChange(q.question_id, 'explanation', val)}
              placeholder="Enter explanation..."
              multiline={true}
              className="ca-expl-plain ca-expl-auto"
              subjectId={subjectId}
              disabled={editLock}
            />
          </div>
        </div>

        {/* RIGHT */}
        <div className="ca-qcard-right">

          <div className="ca-panel-field">
            <label className="ca-label">Module</label>
            <ModuleSelect
              value={moduleVal}
              subjectId={subjectId}
              onChange={val => onChange(q.question_id, 'module_id', val)}
              modules={modules}
              onModuleCreated={onModuleCreated}
            />
          </div>

          <div className="ca-panel-field">
            <label className="ca-label">Correct Answer</label>
            <div className="ca-answer-opts">
              {optLabels.map(lbl => (
                <button key={lbl} type="button"
                  className={`ca-ans-btn ${curAnswer === lbl ? 'ca-ans-btn--active' : ''}`}
                  onClick={() => onChange(q.question_id, 'correct_answer', lbl.toUpperCase())}>
                  {lbl.toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="ca-panel-field">
            <label className="ca-label">Difficulty Level</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DIFF_OPTS.map(d => {
                const active = curDiff === d;
                return (
                  <button key={d} type="button"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      border: active ? diffColors[d].border : '1px solid #e2e8f0',
                      background: active ? diffColors[d].bg : '#f8fafc',
                      color: active ? diffColors[d].color : '#64748b',
                      cursor: 'pointer',
                      transition: 'all .15s',
                    }}
                    onClick={() => onChange(q.question_id, '_diff_and_level', d)}>
                    {toTitleCase(d)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ca-panel-field">
            <label className="ca-label">Bloom's Index</label>
            <select className="ca-select ca-select--full"
              value={bloomsVal}
              onChange={e => onChange(q.question_id, 'blooms_index', e.target.value)}>
              <option value="">— Select —</option>
              {BLOOMS_OPTS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>

          <div className="ca-panel-field">
            <div className="ca-verify-feedback-row">
              <button type="button"
                className={`ca-btn ca-btn--sm ca-feedback-btn ${feedbackSaved ? 'ca-feedback-btn--saved' : ''} ${q.needs_reverification ? 'ca-feedback-btn--readonly' : ''}`}
                onClick={() => setShowFeedback(true)}
                title={q.needs_reverification ? 'View your previously saved feedback (read-only for reverification questions)' : 'Add feedback or notes for this question'}>
                {q.needs_reverification
                  ? (feedbackSaved ? '👁️ View Feedback' : '👁️ View Feedback')
                  : (feedbackSaved ? '💬 Feedback ✓' : '💬 Feedback')}
              </button>
              <label
                className={`ca-toggle-wrap ${!canVerify ? 'ca-toggle-wrap--disabled' : ''}`}
                title={!canVerify ? 'Fill Module, Difficulty Level and Bloom\'s Index before verifying' : ''}>
                <input type="checkbox" className="ca-toggle-cb"
                  checked={!!draft.is_verified}
                  disabled={!canVerify}
                  onChange={e => handleVerifyToggle(e.target.checked)} />
                <span className="ca-toggle-lbl">Verified</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {showFeedback && (
        <FeedbackModal
          question={q}
          existingFeedback={q.feedback}
          readOnly={!!q.needs_reverification}
          onClose={() => setShowFeedback(false)}
          onSaved={() => { setFeedbackSaved(true); setShowFeedback(false); }}
        />
      )}
    </div>
  );
}

// -- Dashboard ----------------------------------------------------------------
function DashboardPage({ user }) {
  const [stats, setStats] = useState({ total:0, verified:0, pending:0, by_me:0, subjects:[] });

  useEffect(() => {
    API.get('/api/verify/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const pct = stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0;

  return (
    <div className="ca-page">
      <div className="ca-page-title">
        <h2>Welcome, {user?.name}</h2>
        <p className="ca-page-sub">Overview of question bank verification progress.</p>
      </div>
      <div className="ca-stat-grid">
        {[
          { label:'Total Questions', value: stats.total,    cls:'total'    },
          { label:'Verified',        value: stats.verified, cls:'verified' },
          { label:'Pending',         value: stats.pending,  cls:'pending'  },
          { label:'Verified By Me',  value: stats.by_me,    cls:'me'       },
        ].map(s => (
          <div key={s.label} className={`ca-stat-card ca-stat-card--${s.cls}`}>
            <p className="ca-stat-label">{s.label}</p>
            <h2 className="ca-stat-val">{s.value}</h2>
          </div>
        ))}
      </div>
      <div className="ca-progress-wrap">
        <div className="ca-progress-hdr"><span>Overall Progress</span><span>{pct}%</span></div>
        <div className="ca-progress-bar"><div className="ca-progress-fill" style={{ width:`${pct}%` }} /></div>
        <p className="ca-progress-sub">{stats.verified} of {stats.total} verified</p>
      </div>
      {stats.subjects?.length > 1 && (
        <div className="ca-subject-breakdown">
          <h3 className="ca-section-title">Subject Breakdown</h3>
          <table className="ca-table">
            <thead>
              <tr><th>Subject</th><th>Total</th><th>Verified</th><th>Pending</th></tr>
            </thead>
            <tbody>
              {stats.subjects.map(s => (
                <tr key={s.subject_id}>
                  <td>{s.name}</td>
                  <td>{s.total}</td>
                  <td>{s.verified}</td>
                  <td>{parseInt(s.total) - parseInt(s.verified)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -- Verify Questions ---------------------------------------------------------
const PAGE_LIMIT = 10;

function VerifyQuestionsPage({ user }) {
  const [examType, setExamType] = useState('');
  const [year,     setYear]     = useState('');
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const [availableYears, setAvailableYears] = useState([]);
  const [yearsLoading,   setYearsLoading]   = useState(false);
  const [modules,        setModules]        = useState([]);

  const [questions,  setQuestions]  = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);

  const [drafts,    setDrafts]    = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');
  const [saveMsgOk, setSaveMsgOk] = useState(true);

  // ✅ Question locking — lockStatusRef tracks each question_id's lock
  // state for this session: 'pending' while the lock call is in flight,
  // 'locked' once confirmed ours, 'denied' if another verifier already
  // holds it. lockSnapshotRef holds the draft as it was right before the
  // first edit, so we can roll back if the lock call comes back 409.
  const lockStatusRef   = useRef({});
  const lockSnapshotRef = useRef({});
  const [lockErrors, setLockErrors] = useState({});

  // ✅ FIX: handleLogout (in the Verify root component) calls
  // localStorage.removeItem('token') BEFORE navigate() unmounts this page,
  // which is what triggers the cleanup below. By the time this effect's
  // cleanup runs, the axios interceptor has nothing to read — the
  // Authorization header goes missing, the unlock request 401s, and the
  // lock silently never clears. Capturing the token now, while we know
  // we're still logged in, and passing it explicitly on the unlock call
  // sidesteps that race entirely.
  const authTokenRef = useRef(localStorage.getItem('token'));

  // Optional courtesy cleanup: if the verifier navigates away (tab switch,
  // logout) without saving, release any locks they're holding so the
  // question doesn't sit stuck for everyone else. Locks the verifier
  // actually saved/verified are left alone — by design they persist.
  useEffect(() => {
    return () => {
      Object.entries(lockStatusRef.current).forEach(([qid, status]) => {
        if (status === 'locked') {
          API.post(
            '/api/verify/questions/unlock',
            { question_id: parseInt(qid) },
            { headers: { Authorization: `Bearer ${authTokenRef.current}` } }
          ).catch(() => {});
        }
      });
    };
  }, []);

  const fallbackSubjectId = getUserSubjectId(user);

  useEffect(() => {
    API.get('/api/verify/modules').then(r => setModules(r.data.modules || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/api/verify/preferences')
      .then(r => {
        const prefs = r.data || {};
        setExamType(prefs.exam_type ?? 'KCET');
        setYear(prefs.year != null ? String(prefs.year) : '');
      })
      .catch(() => {
        setExamType('KCET');
        setYear('');
      })
      .finally(() => setPrefsLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savePrefs = useCallback((et, yr) => {
    API.post('/api/verify/preferences', { exam_type: et, year: yr }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!prefsLoaded) return;
    setYearsLoading(true);
    const params = examType ? { exam_type: examType } : {};
    API.get('/api/verify/years', { params })
      .then(r => setAvailableYears(r.data.years || []))
      .catch(() => setAvailableYears([]))
      .finally(() => setYearsLoading(false));
  }, [prefsLoaded, examType]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildParams = useCallback(() => {
    const p = {};
    if (examType)          p.exam_type  = examType;
    if (year)              p.year       = year;
    if (fallbackSubjectId) p.subject_id = fallbackSubjectId;
    return p;
  }, [examType, year, fallbackSubjectId]);

  const fetchQuestions = useCallback(async (pg = 1, extra = {}) => {
    setLoading(true); setSaveMsg('');
    try {
      const res = await API.get('/api/verify/questions', { params: { page: pg, limit: PAGE_LIMIT, ...extra } });
      const qs         = res.data.questions || [];
      const totalCount = res.data.total || 0;

      setQuestions(qs);
      setTotal(totalCount);
      setTotalPages(res.data.totalPages || 1);
      setPage(pg);

      // ✅ New set of questions on screen — old lock state no longer
      // applies (a question that was 'denied' on a previous page might
      // be free now, and vice versa).
      lockStatusRef.current   = {};
      lockSnapshotRef.current = {};
      setLockErrors({});

      const init = {};
      qs.forEach(q => {
        init[q.question_id] = {
          question_text   : q.question_text    || '',
          option_a        : q.option_a         || '',
          option_b        : q.option_b         || '',
          option_c        : q.option_c         || '',
          option_d        : q.option_d         || '',
          correct_answer  : (q.correct_answer  || '').toUpperCase(),
          explanation     : q.explanation      || '',
          module_id       : String(q.module_id || ''),
          blooms_index    : q.blooms_index      || '',
          difficulty_level: '',
          _diff_db        : q.difficulty_level || '',
          _diff_touched   : false,
          is_verified     : false,
          _dirty          : false,
        };
      });
      setDrafts(init);

      if (extra.year && totalCount === 0 && qs.length === 0) {
        try {
          const yrRes = await API.get('/api/verify/years', { params: { exam_type: extra.exam_type || '' } });
          const yrs = yrRes.data.years || [];
          setAvailableYears(yrs);
          const remaining = yrs.filter(y => String(y) !== String(extra.year));
          if (remaining.length > 0) {
            const nextYear = String(Math.min(...remaining.map(Number)));
            setYear(nextYear);
            savePrefs(extra.exam_type || '', nextYear);
          }
        } catch { /* ignore */ }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [savePrefs]);

  const handleExamTypeChange = async (val) => {
    setExamType(val);
    setYear('');
    savePrefs(val, '');
    setAvailableYears([]);
    if (!val) return;
    setYearsLoading(true);
    try {
      const res = await API.get('/api/verify/years', { params: { exam_type: val } });
      setAvailableYears(res.data.years || []);
    } catch { setAvailableYears([]); }
    finally { setYearsLoading(false); }
  };

  const handleYearChange = (val) => {
    setYear(val);
    savePrefs(examType, val);
  };

  useEffect(() => {
    if (!prefsLoaded) return;
    setDrafts({});
    fetchQuestions(1, buildParams());
  }, [prefsLoaded, examType, year]); // eslint-disable-line

  const handleChange = (qid, field, value) => {
    // Once another verifier's lock has been confirmed, block all further
    // edits on this question outright.
    if (lockStatusRef.current[qid] === 'denied') return;

    const applyFieldChange = () => {
      if (field === '_diff_and_level') {
        setDrafts(prev => ({
          ...prev,
          [qid]: { ...prev[qid], difficulty_level: value, _diff_touched: true, _dirty: true }
        }));
      } else {
        setDrafts(prev => ({
          ...prev,
          [qid]: { ...prev[qid], [field]: value, _dirty: true }
        }));
      }
    };

    // Already locked (or a lock request is already in flight) — just apply.
    if (lockStatusRef.current[qid] === 'locked' || lockStatusRef.current[qid] === 'pending') {
      applyFieldChange();
      return;
    }

    // ── First edit on this question this session ──
    // Snapshot the pre-edit draft so we can roll back if the lock is denied,
    // apply the change optimistically (no typing lag on the common/success
    // path), then fire the lock call in the background.
    lockSnapshotRef.current[qid] = drafts[qid];
    applyFieldChange();
    lockStatusRef.current[qid] = 'pending';

    API.post('/api/verify/questions/lock', { question_id: qid })
      .then(() => {
        lockStatusRef.current[qid] = 'locked';
      })
      .catch(err => {
        lockStatusRef.current[qid] = 'denied';
        const msg = err.response?.data?.error || 'This question is currently being edited by someone else.';
        setLockErrors(prev => ({ ...prev, [qid]: msg }));
        const snap = lockSnapshotRef.current[qid];
        if (snap) setDrafts(prev => ({ ...prev, [qid]: snap }));
      });
  };

  const handleModuleCreated = (mod) =>
    setModules(prev => prev.find(m => m.module_id === mod.module_id) ? prev : [...prev, mod]);

  const dirtyCount = questions.filter(q => {
    const d = drafts[q.question_id] || {};
    return d._dirty || d.is_verified;
  }).length;

  const handleSave = async () => {
    const dirtyQuestions = questions.filter(q => {
      const d = drafts[q.question_id] || {};
      return d._dirty || d.is_verified;
    });
    if (dirtyQuestions.length === 0) {
      setSaveMsgOk(false);
      setSaveMsg('No changes to save yet.');
      setTimeout(() => setSaveMsg(''), 2500);
      return;
    }
    const updates = dirtyQuestions.map(q => {
      const d = drafts[q.question_id] || {};
      return {
        question_id     : q.question_id,
        question_text   : d.question_text,
        option_a        : d.option_a,
        option_b        : d.option_b,
        option_c        : d.option_c,
        option_d        : d.option_d,
        correct_answer  : d.correct_answer  || q.correct_answer,
        explanation     : d.explanation,
        module_id       : d.module_id       !== undefined ? d.module_id       : q.module_id,
        blooms_index    : d.blooms_index     !== undefined ? d.blooms_index    : q.blooms_index,
        difficulty_level: d._diff_touched   ? d.difficulty_level : d._diff_db,
        is_verified     : !!d.is_verified,
        subject_id      : q.subject_id || fallbackSubjectId,
      };
    });
    setSaving(true); setSaveMsg('');
    try {
      const res = await API.post('/api/verify/questions/save', { updates });
      setSaveMsgOk(true);
      setSaveMsg(`Saved ${res.data.saved} question(s)`);
      // Saved questions keep their lock intentionally (per design — it only
      // clears on /unlock or once verified) — mark them 'saved' so the
      // navigate-away cleanup below doesn't release these specific locks.
      updates.forEach(u => { lockStatusRef.current[u.question_id] = 'saved'; });
      setTimeout(() => { fetchQuestions(page, buildParams()); setSaveMsg(''); }, 1800);
    } catch (err) {
      setSaveMsgOk(false);
      setSaveMsg('Save failed: ' + (err.response?.data?.error || 'unknown error'));
    } finally { setSaving(false); }
  };

  if (!prefsLoaded) {
    return <div className="ca-loading" style={{ margin: '60px auto' }}>Loading your preferences…</div>;
  }

  return (
    <div className="ca-page ca-page--verify">
      <div className="ca-page-title">
        <h2>Verify Question Bank <span className="ca-badge">{total} Pending</span></h2>
        <p className="ca-page-sub">Verify as many or as few as you like. Save anytime.</p>
      </div>

      <div className="ca-filters">
        <div className="ca-filter-group">
          <label className="ca-label">Exam Type</label>
          <select className="ca-select" value={examType || ''} onChange={e => handleExamTypeChange(e.target.value)}>
            <option value="">All Types</option>
            {EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="ca-filter-group">
          <label className="ca-label">Year</label>
          {yearsLoading ? (
            <span className="ca-filter-loading">Loading years…</span>
          ) : availableYears.length === 0 ? (
            <span className="ca-filter-empty">No Years Available</span>
          ) : (
            <select className="ca-select" value={year || ''} onChange={e => handleYearChange(e.target.value)}>
              <option value="">All Years</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="ca-qlist-scroll">
        {loading && <div className="ca-loading">Loading questions…</div>}
        {!loading && questions.length === 0 && (
          <div className="ca-empty">
            <p>No Pending Questions Found.</p>
            <p className="ca-empty-sub">All questions verified, or try changing the filters.</p>
          </div>
        )}
        {!loading && questions.map((q, i) => (
          <QuestionCard key={q.question_id} q={q}
            idx={(page - 1) * PAGE_LIMIT + i + 1}
            draft={drafts[q.question_id] || {}}
            onChange={handleChange}
            modules={modules}
            onModuleCreated={handleModuleCreated}
            fallbackSubjectId={fallbackSubjectId}
            lockError={lockErrors[q.question_id]}
          />
        ))}
      </div>

      {!loading && questions.length > 0 && (
        <div className="ca-save-bar">
          {saveMsg && <span className={`ca-save-msg ${saveMsgOk ? 'ok' : 'err'}`}>{saveMsg}</span>}
          {dirtyCount > 0 && !saveMsg && (
            <span className="ca-save-hint">{dirtyCount} question{dirtyCount !== 1 ? 's' : ''} changed</span>
          )}
          <button className="ca-btn ca-btn--primary ca-btn--lg" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : `Save Progress${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
          </button>
        </div>
      )}

      {totalPages > 1 && (
        <div className="ca-pagination">
          <button className="ca-btn"
            onClick={() => fetchQuestions(page-1, buildParams())}
            disabled={page===1||loading}>Prev</button>
          <span className="ca-page-info">Page {page} of {totalPages}</span>
          <button className="ca-btn"
            onClick={() => fetchQuestions(page+1, buildParams())}
            disabled={page===totalPages||loading}>Next</button>
        </div>
      )}
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ question, modules, onModuleCreated, onClose, onSaved }) {
  const q      = question;
  const isReadOnly = [1, 2, 3].includes(Number(q.subject_id));

  const OPT_KEYS   = ['a','b','c','d'];
  const OPT_FIELDS = ['option_a','option_b','option_c','option_d'];

  const [draft, setDraft] = useState({
    question_text   : q.question_text    || '',
    option_a        : q.option_a         || '',
    option_b        : q.option_b         || '',
    option_c        : q.option_c         || '',
    option_d        : q.option_d         || '',
    correct_answer  : (q.correct_answer  || 'A').toUpperCase(),
    explanation     : q.explanation      || '',
    module_id       : String(q.module_id || ''),
    blooms_index    : q.blooms_index      || '',
    difficulty_level: q.difficulty_level || '',
    _diff_touched   : !!q.difficulty_level,
    is_verified     : !!q.is_verified,
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  // ✅ Question locking — lock this question to the current user the
  // moment the edit modal opens. If someone else already holds it, show
  // their name and block saving. If we close without saving, release the
  // lock (saving intentionally leaves the lock in place).
  const [lockDenied, setLockDenied] = useState(false);
  const savedRef    = useRef(false);
  // Same token-timing race as VerifyQuestionsPage — capture it now in case
  // this unmounts as part of a logout.
  const authTokenRef = useRef(localStorage.getItem('token'));

  useEffect(() => {
    let cancelled = false;
    API.post('/api/verify/questions/lock', { question_id: q.question_id })
      .catch(err => {
        if (cancelled) return;
        setLockDenied(true);
        setErr(err.response?.data?.error || 'This question is currently being edited by someone else.');
      });
    return () => {
      cancelled = true;
      if (!savedRef.current) {
        API.post(
          '/api/verify/questions/unlock',
          { question_id: q.question_id },
          { headers: { Authorization: `Bearer ${authTokenRef.current}` } }
        ).catch(() => {});
      }
    };
  }, [q.question_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showFeedback,  setShowFeedback]  = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(
    !!(q.feedback?.note || q.feedback?.feedback_pdf)
  );

  const set = (field, value) => {
    if (field === '_diff_and_level') {
      setDraft(prev => ({ ...prev, difficulty_level: value, _diff_touched: true }));
    } else {
      setDraft(prev => ({ ...prev, [field]: value }));
    }
  };

  const canVerify = !!(draft.module_id && draft.difficulty_level && draft.blooms_index);

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      await API.post('/api/verify/questions/save', {
        updates: [{ question_id: q.question_id, subject_id: q.subject_id, ...draft }]
      });
      savedRef.current = true;
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const curAnswer = draft.correct_answer.toLowerCase();
  const curDiff   = draft.difficulty_level;

  return (
    <div className="ca-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ca-modal ca-edit-modal-v2">

        <div className="ca-modal-head ca-edit-head">
          <div className="ca-edit-head-left">
            <span className="ca-edit-pencil">✏️</span>
            <span className="ca-edit-title">
              Edit — Q{q.question_number ?? q.question_id}
            </span>
          </div>
          <button className="ca-modal-close" onClick={onClose}>✕</button>
        </div>

        {err && <div className="ca-edit-err">{err}</div>}

        <div className="ca-modal-body ca-edit-body">
          <div className="ca-edit-two-col">

            <div className="ca-edit-left">

              <div className="ca-edit-section">
                <div className="ca-edit-field-label">Question</div>
                <div className="ca-edit-content-box ca-edit-content-box--ro">
                  {hasMath(draft.question_text, q.subject_id)
                    ? <MathRenderer text={draft.question_text} subjectId={q.subject_id} block={true} />
                    : <span className="ca-edit-plain-text">{draft.question_text}</span>}
                </div>
              </div>

              <div className="ca-edit-section">
                <div className="ca-edit-field-label">Options</div>
                <div className="ca-edit-opts-grid">
                  {OPT_KEYS.map((opt, i) => {
                    const fieldKey  = OPT_FIELDS[i];
                    const val       = draft[fieldKey];
                    const isCorrect = curAnswer === opt;
                    return (
                      <div key={opt} className={`ca-edit-opt-cell ${isCorrect ? 'ca-edit-opt-cell--correct' : ''}`}>
                        <span className="ca-edit-opt-lbl">{opt.toLowerCase()})</span>
                        <MathField
                          value={val}
                          onChange={v => set(fieldKey, v)}
                          placeholder={`Option ${opt.toUpperCase()}`}
                          multiline={false}
                          className="ca-edit-opt-input"
                          subjectId={q.subject_id}
                          disabled={isReadOnly}
                        />
                        {isCorrect && <span className="ca-edit-opt-check">✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="ca-edit-section">
                <div className="ca-edit-field-label">Explanation</div>
                <div className={`ca-edit-content-box ${isReadOnly ? 'ca-edit-content-box--ro' : ''}`}>
                  <MathField
                    value={draft.explanation}
                    onChange={val => set('explanation', val)}
                    placeholder="Explanation…"
                    multiline={true}
                    className="ca-edit-textarea"
                    subjectId={q.subject_id}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>

            <div className="ca-edit-right">

              <div className="ca-edit-ctrl-field">
                <div className="ca-edit-ctrl-label">Module</div>
                <ModuleSelect
                  value={draft.module_id}
                  subjectId={q.subject_id}
                  onChange={val => set('module_id', val)}
                  modules={modules}
                  onModuleCreated={mod => {
                    if (onModuleCreated) onModuleCreated(mod);
                    set('module_id', String(mod.module_id));
                  }}
                />
              </div>

              <div className="ca-edit-ctrl-field">
                <div className="ca-edit-ctrl-label">Correct Answer</div>
                <div className="ca-edit-ans-btns">
                  {OPT_KEYS.map(opt => (
                    <button key={opt} type="button"
                      className={`ca-edit-ans-btn ${curAnswer === opt ? 'ca-edit-ans-btn--active' : ''}`}
                      onClick={() => set('correct_answer', opt.toUpperCase())}>
                      {opt.toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ca-edit-ctrl-field">
                <div className="ca-edit-ctrl-label">Difficulty Level</div>
                <div className="ca-edit-diff-row">
                  {['low','medium','high'].map(d => (
                    <button key={d} type="button"
                      className={`ca-edit-diff-btn ca-edit-diff-btn--${d} ${curDiff === d ? 'ca-edit-diff-btn--active' : ''}`}
                      onClick={() => set('_diff_and_level', d)}>
                      {toTitleCase(d)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ca-edit-ctrl-field">
                <div className="ca-edit-ctrl-label">Bloom's Index</div>
                <select
                  className="ca-select ca-select--full"
                  value={draft.blooms_index}
                  onChange={e => set('blooms_index', e.target.value)}>
                  <option value="">— Select —</option>
                  {BLOOMS_OPTS.map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
<div className="ca-edit-ctrl-field">
<div className="ca-edit-ctrl-label">Feedback</div>
<button
type="button"
className={`ca-btn ca-btn--sm ca-feedback-btn ${feedbackSaved ? 'ca-feedback-btn--saved' : ''}`}
onClick={() => setShowFeedback(true)}
title={
  (q.needs_reverification === true || q.needs_reverification === 'true' ||
   q.was_reverified === true || q.was_reverified === 'true')
    ? 'View feedback (read-only for reverified questions)'
    : 'View, replace, or add feedback PDF / note for this question'
}
>
{(q.needs_reverification === true || q.needs_reverification === 'true' ||
  q.was_reverified === true || q.was_reverified === 'true')
  ? '👁️ View Feedback'
  : (feedbackSaved ? '💬 Feedback ✓' : '💬 Add Feedback')}
</button>
</div>

              <div className="ca-edit-ctrl-field">
                <label
                  className={`ca-edit-verified-toggle ${!canVerify ? 'ca-toggle-wrap--disabled' : ''}`}
                  title={!canVerify ? 'Fill Module, Difficulty Level and Bloom\'s Index before verifying' : ''}>
                  <input
                    type="checkbox"
                    className="ca-toggle-cb"
                    checked={draft.is_verified}
                    disabled={!canVerify}
                    onChange={e => set('is_verified', e.target.checked)}
                  />
                  <span className="ca-toggle-lbl">Verified</span>
                </label>
              </div>

            </div>
          </div>
        </div>

        <div className="ca-modal-foot">
          {err && <span className="ca-err-inline">{err}</span>}
          <button className="ca-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ca-btn ca-btn--primary ca-edit-save-btn" onClick={handleSave} disabled={saving || lockDenied}>
            {saving ? 'Saving…' : '💾 Save Changes'}
          </button>
        </div>
      </div>

{showFeedback && (
<FeedbackModal
question={q}
existingFeedback={q.feedback}
readOnly={
  q.needs_reverification === true || q.needs_reverification === 'true' ||
  q.was_reverified === true || q.was_reverified === 'true'
}
onClose={() => setShowFeedback(false)}
onSaved={() => { setFeedbackSaved(true); setShowFeedback(false); }}
/>
)}
</div>

  );
}


// -- History ------------------------------------------------------------------
function HistoryPage() {
  const [history,    setHistory]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [modules,    setModules]    = useState([]);

  const fetchHistory = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await API.get('/api/verify/history', { params: { page: pg, limit: PAGE_LIMIT } });
      setHistory(res.data.history || []);
      setTotal(res.data.total     || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pg);
    } catch { /**/ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchHistory(1);
    API.get('/api/verify/modules').then(r => setModules(r.data.modules || [])).catch(() => {});
  }, [fetchHistory]);

  const handleModuleCreated = mod =>
    setModules(prev => prev.find(m => m.module_id === mod.module_id) ? prev : [...prev, mod]);

  return (
    <div className="ca-page">
      <div className="ca-page-title">
        <h2>Verification History <span className="ca-badge">{total} Questions</span></h2>
        <p className="ca-page-sub">All questions you have previously verified. Click Edit to modify.</p>
      </div>

      {loading && <div className="ca-loading">Loading…</div>}
      {!loading && history.length === 0 && (
        <div className="ca-empty"><p>No Verification History Yet.</p></div>
      )}

      {!loading && history.length > 0 && (
        <div className="ca-hist-table">
          {/* ── Header ── */}
          <div className="ca-hist-hdr">
            <div className="ca-hist-col ca-hist-col--no">#</div>
            <div className="ca-hist-col ca-hist-col--q">Question</div>
            <div className="ca-hist-col ca-hist-col--year">Year</div>
            <div className="ca-hist-col ca-hist-col--date">Verified On</div>
            <div className="ca-hist-col ca-hist-col--hod">Approved By HOD</div>
            <div className="ca-hist-col ca-hist-col--action">Action</div>
          </div>

          {/* ── Rows ── */}
          {history.map((h) => (
            <div key={h.question_id} className="ca-hist-row">
              <div className="ca-hist-col ca-hist-col--no">{h.question_number ?? h.question_id}</div>
              <div className="ca-hist-col ca-hist-col--q">
                {hasMath(h.question_text, h.subject_id)
                  ? <MathRenderer text={h.question_text} subjectId={h.subject_id} block={true} className="ca-hist-qtext" />
                  : <p className="ca-hist-qtext">{h.question_text}</p>
                }
                {h.module_name && <span className="ca-tag ca-tag--sm">{h.module_name}</span>}
              </div>
              <div className="ca-hist-col ca-hist-col--year">{h.exam_year || '—'}</div>
              <div className="ca-hist-col ca-hist-col--date">
                {h.verified_at ? new Date(h.verified_at).toLocaleDateString('en-IN') : '—'}
              </div>
              <div className="ca-hist-col ca-hist-col--hod">
                {h.hod_approved ? (
                  <span className="ca-hod-pill ca-hod-pill--yes">
                    <span className="ca-hod-name">{h.hod_approved_by_name || 'Approved'}</span>
                    {h.hod_approved_at && (
                      <span className="ca-hod-date">{new Date(h.hod_approved_at).toLocaleDateString('en-IN')}</span>
                    )}
                  </span>
                ) : (
                  <span className="ca-hod-pill ca-hod-pill--pending">Pending</span>
                )}
              </div>
              <div className="ca-hist-col ca-hist-col--action">
                <button className="ca-btn ca-btn--sm ca-btn--outline" onClick={() => setEditing(h)}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="ca-pagination">
          <button className="ca-btn" onClick={() => fetchHistory(page-1)} disabled={page===1||loading}>Prev</button>
          <span className="ca-page-info">Page {page} of {totalPages}</span>
          <button className="ca-btn" onClick={() => fetchHistory(page+1)} disabled={page===totalPages||loading}>Next</button>
        </div>
      )}

      {editing && (
        <EditModal question={editing} modules={modules}
          onModuleCreated={handleModuleCreated}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchHistory(page); }} />
      )}
    </div>
  );
}

// -- Root ---------------------------------------------------------------------
export default function Verify() {
  const navigate = useNavigate();
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const [view, setView] = useState('dashboard');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="ca-layout">
      <Header user={user} onLogout={handleLogout} />
      <NavTabs view={view} setView={setView} />
      <main className="ca-main">
        {view === 'dashboard' && <DashboardPage user={user} />}
        {view === 'verify'    && <VerifyQuestionsPage user={user} />}
        {view === 'history'   && <HistoryPage />}
      </main>
    </div>
  );
}